import React, { useState, useEffect } from 'react';
import { useAuthenticatedFetch } from '../../utils/api';

/**
 * Step 1: Choose compression type.
 */
function CompressionTypeStep({ selectedTypeId, onSelect, onNext }) {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [types, setTypes]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    authenticatedFetch('/api/optimization-types')
      .then(res => res.json())
      .then(data => {
        if (cancelled) return;
        if (data.success && data.types) {
          setTypes(data.types);
          // Default to regular
          if (!selectedTypeId) {
            const reg = data.types.find(t => t.key === 'regular');
            if (reg) onSelect(reg);
          }
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [authenticatedFetch, onSelect, selectedTypeId]);

  if (loading) {
    return (
      <s-stack gap="large">
        {/* Matches the real heading block */}
        <div style={{ textAlign: 'center' }}>
          <div className="shimmer-loader" style={{ height: 24, width: '40%', borderRadius: 4, margin: '0 auto' }} />
          <div className="shimmer-loader" style={{ height: 20, width: '30%', borderRadius: 4, margin: '8px auto 0' }} />
        </div>

        {/* Matches the real compression-types-container grid — 3 cards at 140px */}
        <div className="compression-types-container">
          {[1, 2, 3].map(i => (
            <div key={i} className="compression-type-card-shimmer">
              <div className="shimmer-line shimmer-compression-title" />
              <div className="shimmer-line shimmer-compression-description" />
            </div>
          ))}
        </div>

        {/* Matches the Continue button */}
        <div className="compression-type-button-container">
          <div className="shimmer-loader" style={{ height: 40, width: 220, borderRadius: 6 }} />
        </div>
      </s-stack>
    );
  }

  return (
    <s-stack gap="large">
      <div style={{ textAlign: 'center' }}>
        <p style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#202223' }}>
          Choose your default compression level
        </p>
        <p style={{ margin: '6px 0 0', fontSize: '14px', fontWeight: 600, color: '#202223' }}>
          You can change this anytime in settings.
        </p>
      </div>

      <div className="compression-types-container">
        {types.map(type => {
          const isSelected = selectedTypeId === type.id;
          return (
            <div
              key={type.id}
              className={`compression-type-card ${isSelected ? 'selected' : ''}`}
              onClick={() => onSelect(type)}
            >
              <div className="compression-type-card-content">
                <div className="compression-type-header">
                  <s-text type="strong">{type.title}</s-text>
                  {isSelected && (
                    <div className="compression-type-selected-indicator">✓</div>
                  )}
                </div>
                <s-text size="small" color="subdued" className="compression-type-description">
                  {type.description}
                </s-text>
                <s-text size="small" color="subdued" style={{ marginTop: 'auto' }}>
                  Quality: {type.quality}% · Max width: {type.maxWidth}px
                </s-text>
              </div>
            </div>
          );
        })}
      </div>

      <div className="compression-type-button-container">
        <s-button
          variant="primary"
          size="large"
          onClick={onNext}
          disabled={!selectedTypeId}
        >
          Continue to Next Step
        </s-button>
      </div>
    </s-stack>
  );
}

export default CompressionTypeStep;
